import { ReactNode } from 'react'
import {
  Box,
  Checkbox,
  CircularProgress,
  Radio,
  TextField,
  Typography,
} from '@mui/material'
import { InputAdornment } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AvailableUser } from '../../createActionPlan/types'
import { icons } from '../../../../../utils/icons'
import { useStyles } from './userSelectionColumn.styles'

export interface UserSelectionColumnProps {
  title: string
  users: AvailableUser[]
  isLoading: boolean
  hasError: boolean
  searchValue: string
  onSearchChange: (value: string) => void
  selectionMode: 'single' | 'multiple'
  selectedIds: Set<number>
  onSelectUser: (user: AvailableUser) => void
  filterButton: ReactNode
  error?: string
  selectAllLabel?: string
  onSelectAll?: () => void
}

const UserSelectionColumn = ({
  title,
  users,
  isLoading,
  hasError,
  searchValue,
  onSearchChange,
  selectionMode,
  selectedIds,
  onSelectUser,
  filterButton,
  error,
  selectAllLabel,
  onSelectAll,
}: UserSelectionColumnProps) => {
  const { t } = useTranslation()
  const styles = useStyles()

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={styles.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    if (hasError) {
      return (
        <Typography sx={styles.errorText}>
          {t('actionPlan.errorLoadingPlanInfo')}
        </Typography>
      )
    }

    if (!users.length) {
      return (
        <Typography sx={styles.noData}>
          {t('actionPlan.noAvailableUsers')}
        </Typography>
      )
    }

    return users.map((user) => {
      const isSelected = selectedIds.has(user.id)

      return (
        <Box
          key={user.id}
          sx={styles.userItem}
          onClick={() => onSelectUser(user)}
        >
          {selectionMode === 'single' ? (
            <Radio
              checked={isSelected}
              onChange={() => onSelectUser(user)}
              color='primary'
              sx={{
                padding: '2px',
                '& svg': { width: '26px', height: '26px' }
              }}
            />
          ) : (
            <Checkbox
              checked={isSelected}
              onChange={() => onSelectUser(user)}
              onClick={(event) => event.stopPropagation()}
              color='primary'
              sx={{
                padding: '2px',
                '& .MuiBox-root': { width: '24px', height: '24px' }
              }}
            />
          )}
          <Box sx={styles.userItemInfo}>
            <Typography sx={styles.userItemName}>{user.fullName}</Typography>
            <Typography sx={styles.userItemDetail}>
              {t('actionPlan.role')}: {user.role} - {t('actionPlan.userType')}: {user.userType}
            </Typography>
          </Box>
        </Box>
      )
    })
  }

  const areAllSelected = users.length > 0 && users.every((user) => selectedIds.has(user.id))

  return (
    <Box sx={styles.column}>
      <Box sx={styles.columnHeader}>
        <Typography sx={styles.columnTitle}>{title}</Typography>
        {filterButton}
      </Box>

      <Box sx={styles.userListContainer}>
        <Box sx={styles.searchWrapper}>
          <TextField
            fullWidth
            placeholder={t('actionPlan.manualCreation.step2.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  {icons('search')}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {selectionMode === 'multiple' && onSelectAll && (
          <Box sx={styles.selectAllRow} onClick={onSelectAll}>
            <Checkbox
              checked={areAllSelected}
              onChange={onSelectAll}
              onClick={(event) => event.stopPropagation()}
              color='primary'
              sx={{
                padding: '2px',
                '& .MuiBox-root': { width: '24px', height: '24px' }
              }}
            />
            <Typography sx={styles.selectAllText}>
              {selectAllLabel}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography sx={styles.errorText}>{error}</Typography>
        )}

        {renderContent()}
      </Box>
    </Box>
  )
}

export default UserSelectionColumn
